import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ssoLogin, validateToken } from "../api/ssoAuth";
import {
  getRedirectUri,
  getAppName,
  getAppConfig,
  redirectToApp,
  isRedirectHostnameTrusted,
} from "../utils/redirectHelper";
import { useDomain } from "../contexts/DomainContext";
import CompanyLogo from "../components/CompanyLogo";
import AuthFooter from "../components/AuthFooter";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import useLogoutListener from "../hooks/useLogoutListener";
import { getDeviceId } from "../services/mqttLogoutService";
import { getDisplayCompanyName } from "../utils/branding";
import "./Login.css";

/**
 * Decode a JWT payload. Returns null when the token is not a readable JWT
 * (the auth API may hand out opaque tokens - those are not decodable here,
 * which is not the same thing as being invalid).
 */
const decodeJwtPayload = (token) => {
  try {
    if (!token || typeof token !== "string") {
      return null;
    }

    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }

    // JWT payloads are base64url encoded and may drop the padding
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);

    return JSON.parse(atob(padded));
  } catch (error) {
    console.warn("Could not decode token payload:", error);
    return null;
  }
};

/**
 * True only when we can PROVE the token is expired.
 * An opaque (non-JWT) token is not "expired" - only the server can tell,
 * so we let Authorize/ValidateToken decide instead of dropping the session.
 */
const isTokenExpired = (token) => {
  const payload = decodeJwtPayload(token);

  if (!payload || !payload.exp) {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  const expired = payload.exp <= now;

  if (expired) {
    console.log("Token expired. Exp:", payload.exp, "Now:", now);
  }

  return expired;
};

/**
 * True only when the token is a readable JWT whose exp is still in the future.
 * Used for the "auth server unreachable" fallback, where we need positive proof
 * that the session is still good before redirecting anyway.
 */
const isJwtStillValid = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) {
    return false;
  }
  return payload.exp > Math.floor(Date.now() / 1000);
};

const Login = () => {
  const navigate = useNavigate();
  const { domainConfig, domainLoading } = useDomain();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  // React StrictMode runs effects twice in dev - make sure the silent-SSO check
  // (and its redirect) only fires once per page load.
  const hasCheckedSession = useRef(false);

  const redirectUri = getRedirectUri();
  const appName = getAppName();
  let appConfig = getAppConfig();
  const displayCompanyName = getDisplayCompanyName(domainConfig?.companyName);
  const displayAppName = getDisplayCompanyName(appName, "CRM");

  // Setup logout listener - when another app logs out, redirect to login
  useLogoutListener((logoutEvent) => {
    console.log("🚪 Logout event received, logging out:", logoutEvent);
    // Clear session and redirect to login
    localStorage.removeItem("kit19_sso_token");
    localStorage.removeItem("kit19_user_data");
    // Keep device ID for next session
    window.location.href = "/login";
  });

  // Extract domain from redirect URL or use window location
  const extractDomain = () => {
    if (redirectUri) {
      try {
        const url = new URL(redirectUri);
        return url.hostname;
      } catch (e) {
        console.warn("Could not parse redirect URI:", redirectUri);
      }
    }
    return window.location.hostname;
  };

  // Add redirected domain to appConfig for login
  const getLoginConfig = () => {
    const domain = extractDomain();
    const baseConfig = appConfig || {};
    return {
      ...baseConfig,
      refDomain: domain, // Pass the redirected domain URL
    };
  };

  // Update favicon dynamically
  const updateFavicon = (iconUrl) => {
    if (!iconUrl) return;

    // Remove existing favicon
    const existingLink = document.querySelector("link[rel~='icon']");
    if (existingLink) {
      existingLink.href = iconUrl;
    } else {
      // Create new favicon link
      const link = document.createElement("link");
      link.rel = "icon";
      link.href = iconUrl;
      document.head.appendChild(link);
    }
  };

  useEffect(() => {
    // Initialize device ID on first load

    const deviceId = getDeviceId();
    console.log("📱 Device ID:", deviceId);

    const params = new URLSearchParams(window.location.search);
    const hasRedirectUri =
      params.get("redirect_uri") || params.get("redirectUri");

    if (!hasRedirectUri) {
      console.warn("Direct access blocked - no redirect_uri parameter");
      setAccessBlocked(true);
      return;
    }

    if (!redirectUri) {
      console.warn("Direct access blocked - redirect_uri is malformed or uses a disallowed protocol");
      setAccessBlocked(true);
      return;
    }

    // Wait for the domain lookup (DomainContext) to finish before deciding
    // whether this redirect target is trusted - partner domains on their own
    // custom domain are only known once Authorize/DomainInfo has responded.
    if (domainLoading) {
      return;
    }

    if (!isRedirectHostnameTrusted(redirectUri, domainConfig)) {
      console.warn(
        "Direct access blocked - redirect_uri hostname is not a recognized app or partner domain",
      );
      setAccessBlocked(true);
      return;
    }

    // Log received configuration for debugging
    if (appConfig) {
      console.log("Received SSO Configuration:", appConfig);
    }

    // Check if user already has valid session token in SSO server
    const checkExistingSession = async () => {
      try {
        // Check Kit19SSO's own localStorage for existing token
        const existingToken = localStorage.getItem("kit19_sso_token");
        console.log(
          "🔍 Checking for existing token:",
          existingToken ? "✅ Found" : "❌ Not found",
        );

        if (existingToken && redirectUri) {
          // Only drop the session when the token is PROVABLY expired - anything
          // else is the auth server's call, made by validateAndRedirect below.
          if (isTokenExpired(existingToken)) {
            console.log("❌ Token is expired");
            clearSsoSession();
            return;
          }

          console.log(
            "🔐 Existing SSO session found, validating and redirecting...",
          );
          setIsRedirecting(true);

          const isValid = await validateAndRedirect(existingToken);

          if (!isValid) {
            console.log("⚠️ Token validation failed, showing login form");
            setIsRedirecting(false);
            clearSsoSession();
          }
          // If isValid, redirect should happen, no need to clear state
        } else {
          if (!existingToken) {
            console.log("ℹ️ No token in localStorage");
          }
          if (!redirectUri) {
            console.log("⚠️ No redirect URI provided");
          }
        }
      } catch (error) {
        console.error("❌ Error checking existing session:", error);
        setIsRedirecting(false);
      }
    };

    if (hasCheckedSession.current) {
      return;
    }
    hasCheckedSession.current = true;

    checkExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainLoading, domainConfig]);

  // Clear the SSO session, but keep kit19_device_id: that id pairs this browser
  // across every Kit19 app for MQTT logout broadcasting, and must survive a
  // failed/expired session or cross-app logout silently stops working.
  const clearSsoSession = () => {
    localStorage.removeItem("kit19_sso_token");
    localStorage.removeItem("kit19_user_data");
  };

  // Hand the existing session back to the calling app. Built through
  // redirectToApp so a silent SSO redirect carries exactly the same params
  // (token, user_id, sso, deviceId, config) as a redirect after a fresh login.
  const redirectWithExistingSession = (token, validatedDetails) => {
    let storedUser = null;
    try {
      storedUser = JSON.parse(localStorage.getItem("kit19_user_data"));
    } catch (e) {
      console.warn("Could not parse stored user data:", e);
    }

    const userInfo = validatedDetails?.userInfo || {};
    const userId =
      userInfo.userId || storedUser?.userId || storedUser?.User_ID || "";

    redirectToApp(
      redirectUri,
      token,
      { ...(storedUser || {}), userId, User_ID: userId },
      appConfig,
      getDeviceId(),
    );
  };

  const validateAndRedirect = async (token) => {
    try {
      console.log("Validating token:", token.substring(0, 20) + "...");

      if (!redirectUri) {
        console.error("No redirect URI available");
        return false;
      }

      // Provably expired tokens never reach the server
      if (isTokenExpired(token)) {
        console.log("Token is expired based on JWT payload");
        return false;
      }

      const result = await validateToken(token);

      console.log("Token validation response:", result);

      if (result.valid) {
        console.log("Token is valid, preparing redirect...");
        redirectWithExistingSession(token, result.userData);
        return true;
      }

      // If the validation API itself is unreachable (not just "invalid token"),
      // and the JWT proves it isn't expired, fall back to a direct redirect.
      if (result.networkError && isJwtStillValid(token)) {
        console.warn(
          "Token validation API unreachable but token is valid, redirecting anyway:",
          result.message,
        );
        redirectWithExistingSession(token, null);
        return true;
      }

      console.log("Token validation failed:", result.message);
      return false;
    } catch (error) {
      console.error("Token validation error:", error);
      return false;
    }
  };

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!credentials.username || !credentials.password) {
      toast.error("Please enter username and password");
      return;
    }

    setLoading(true);

    try {
      console.log("Login attempt with redirect URI:", redirectUri);
      console.log("App name:", appName);
      console.log("App config:", appConfig);

      const loginConfig = getLoginConfig();
      console.log("Login config with domain:", loginConfig);

      const response = await ssoLogin(
        credentials.username,
        credentials.password,
        appName,
        loginConfig,
      );

      console.log("Login response:", {
        success: response.success,
        hasToken: !!response.token,
        message: response.message,
      });

      if (response.success && response.token) {
        // Store token based on config or default
        const tokenKey = appConfig?.tokenKey || "kit19_sso_token";
        localStorage.setItem(tokenKey, response.token);
        localStorage.setItem("kit19_sso_token", response.token);
        localStorage.setItem(
          "kit19_user_data",
          JSON.stringify(response.userData),
        );

        // Store configuration for logout redirect
        if (appConfig) {
          localStorage.setItem("kit19_sso_config", JSON.stringify(appConfig));
        }

        toast.success("Login successful! Redirecting...");

        // Redirect back to the app or default dashboard
        if (redirectUri && isRedirectHostnameTrusted(redirectUri, domainConfig)) {
          console.log("Redirecting to app in 1 second...");
          const deviceId = localStorage.getItem("kit19_device_id") || "";
          setTimeout(() => {
            redirectToApp(
              redirectUri,
              response.token,
              response.userData,
              appConfig,
              deviceId,
            );
          }, 1000);
        } else {
          // No redirect URI, show success
          console.log("No redirect URI - staying on SSO page");
          toast.info("Login successful! Please return to your application.");
        }
      } else {
        if (response.details && response.details.isEmailVerified === false) {
          const { email, companyName, userId } = response.details || {};
          if (email) localStorage.setItem("kit19_pending_email", email);
          if (companyName)
            localStorage.setItem("kit19_pending_company", companyName);
          if (userId) localStorage.setItem("kit19_user_id", userId);

          // Navigate to email verification page, preserving URL parameters
          navigate("/verify-email" + window.location.search, {
            state: {
              email: email || credentials.username,
              companyName: response.details?.companyName || "",
              userId: userId,
            },
          });
          return;
        } else {
          toast.error(response.message || "Login failed");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.message || "Authentication failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="sso-login-container"
      style={{
        backgroundImage: domainConfig?.backgroundImage
          ? `url(${domainConfig.backgroundImage})`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="login-card">
        {domainLoading ? (
          // Show loading screen while domain info is being fetched
          <div
            className="domain-loading-container"
            style={{ textAlign: "center", padding: "40px" }}
          >
            <div style={{ fontSize: "48px", marginBottom: "20px" }}>📋</div>
            <h2>Preparing Login Page...</h2>
            <p style={{ color: "#666", marginTop: "10px" }}>
              Setting up your secure login environment
            </p>
            <div style={{ marginTop: "20px" }}>
              <div
                style={{
                  display: "inline-block",
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #667eea",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : isRedirecting ? (
          // Show redirecting message
          <div
            className="redirecting-container"
            style={{ textAlign: "center", padding: "40px" }}
          >
            <div style={{ fontSize: "48px", marginBottom: "20px" }}>⏳</div>
            <h2>Verifying your session...</h2>
            <p style={{ color: "#666", marginTop: "10px" }}>
              Redirecting to your application
            </p>
            <div style={{ marginTop: "20px" }}>
              <div
                style={{
                  display: "inline-block",
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #667eea",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : accessBlocked ? (
          // Show access blocked message
          <div className="access-blocked">
            <div className="login-header">
              {/* <img
                src="/icon.png"
                alt={`${displayCompanyName} SSO`}
                className="company-logo"
                style={{
                  maxWidth: "200px",
                  maxHeight: "80px",
                  marginBottom: "16px",
                }}
              /> */}
              <CompanyLogo
                appConfig={appConfig}
                domainConfig={domainConfig}
                appName={appName}
              />
              <p className="subtitle">Access Denied</p>
            </div>

            <div className="blocked-message">
              <div className="blocked-icon">🔒</div>
              <h3>Direct Access Not Allowed</h3>
              <p>This SSO login page cannot be accessed directly.</p>
              <p>Please access this page through an authorized application.</p>
            </div>

            <div className="login-footer">
              <p className="help-text" style={{ marginTop: "12px" }}>
                Protected by {displayCompanyName} SSO | All rights reserved
              </p>
            </div>
          </div>
        ) : (
          // Show normal login form
          <>
            <div className="login-header">
              <CompanyLogo
                appConfig={appConfig}
                domainConfig={domainConfig}
                appName={appName}
              />
              <p className="subtitle">
                {domainConfig?.slogan || "Single Sign-On Portal"}
              </p>
              {appName && displayAppName !== "CRM" && (
                <p className="app-info">
                  Signing in to <strong>{displayAppName}</strong>
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  className="form-control"
                  placeholder="Enter your username"
                  value={credentials.username}
                  onChange={handleChange}
                  autoComplete="username"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className="form-control"
                    placeholder="Enter your password"
                    value={credentials.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <div style={{ textAlign: "right", marginTop: "8px" }}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      // Preserve URL parameters when navigating to forgot password
                      const currentSearch = window.location.search;
                      navigate("/forgot-password" + currentSearch);
                    }}
                    style={{ fontSize: "14px", color: "#667eea" }}
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <AuthFooter
              domainConfig={domainConfig}
              showSignUp={true}
              onNavigate={(path) => {
                const currentSearch = window.location.search;
                navigate(path + currentSearch);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
